import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import Navbar from './components/Navbar';
import Home from './components/Home';
import CheatSheet from './components/CheatSheet';
import DealCalculator from './components/DealCalculator';
import AuctionTracker from './components/AuctionTracker';
import AuctionPlatforms from './components/AuctionPlatforms';
import DueDiligence from './components/DueDiligence';
import Auth from './components/Auth';
import SetNewPassword from './components/SetNewPassword';
import AIResearch from './components/AIResearch';

function App() {
  const [activePage, setActivePage] = useState('Home');
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [savedDeals, setSavedDeals] = useState([]);

  const loadDeals = useCallback(async (userId) => {
    const { data, error } = await supabase
      .from('deals')
      .select('*')
      .eq('user_id', userId)
      .order('saved_at', { ascending: false });
    if (!error && data) {
      setSavedDeals(data.map(d => ({
        id: d.id,
        type: d.type,
        address: d.address,
        status: d.status,
        inputs: d.inputs,
        metrics: d.metrics,
        notes: d.notes,
        apn: d.apn,
        irsLien: d.irs_lien,
        titleIssues: d.title_issues,
        docLinks: d.doc_links,
        comp1Address: d.comp1_address, comp1Price: d.comp1_price, comp1Sqft: d.comp1_sqft,
        comp2Address: d.comp2_address, comp2Price: d.comp2_price, comp2Sqft: d.comp2_sqft,
        comp3Address: d.comp3_address, comp3Price: d.comp3_price, comp3Sqft: d.comp3_sqft,
        savedAt: d.saved_at,
      })));
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) loadDeals(session.user.id);
      setLoading(false);
    });

  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
  if (_event === 'PASSWORD_RECOVERY') {
    setPasswordRecovery(true);
  }
  setSession(session);
  if (session) loadDeals(session.user.id);
  else setSavedDeals([]);
});

    return () => subscription.unsubscribe();
  }, [loadDeals]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const handleSaveDeal = async (deal) => {
    if (!session) {
      setSavedDeals(prev => [{ ...deal, id: Date.now().toString(), savedAt: new Date().toISOString(), status: 'Researching' }, ...prev]);
      setActivePage('Due Diligence');
      return;
    }

    const { data, error } = await supabase.from('deals').insert({
      user_id: session.user.id,
      type: deal.type,
      address: deal.address || '',
      status: 'Researching',
      inputs: deal.inputs,
      metrics: deal.metrics,
    }).select().single();

    if (!error && data) {
      setSavedDeals(prev => [{
        id: data.id,
        type: data.type,
        address: data.address,
        status: data.status,
        inputs: data.inputs,
        metrics: data.metrics,
        savedAt: data.saved_at,
      }, ...prev]);
    } else {
      console.error('Save failed:', error);
      alert('Failed to save deal. Please try again.');
    }
    setActivePage('Due Diligence');
  };

  const handleUpdateDeal = async (id, updates) => {
    if (session) {
      await supabase.from('deals').update({
        status: updates.status,
        notes: updates.notes,
        apn: updates.apn,
        irs_lien: updates.irsLien,
        title_issues: updates.titleIssues,
        doc_links: updates.docLinks,
        address: updates.address,
        comp1_address: updates.comp1Address, comp1_price: updates.comp1Price, comp1_sqft: updates.comp1Sqft,
        comp2_address: updates.comp2Address, comp2_price: updates.comp2Price, comp2_sqft: updates.comp2Sqft,
        comp3_address: updates.comp3Address, comp3_price: updates.comp3Price, comp3_sqft: updates.comp3Sqft,
        updated_at: new Date().toISOString(),
      }).eq('id', id).eq('user_id', session.user.id);
    }
    setSavedDeals(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
  };

  const handleDeleteDeal = async (id) => {
    if (session) {
      await supabase.from('deals').delete().eq('id', id).eq('user_id', session.user.id);
    }
    setSavedDeals(prev => prev.filter(d => d.id !== id));
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5' }}>
        <p style={{ color: '#888', fontSize: '16px' }}>Loading...</p>
      </div>
    );
  }

  if (passwordRecovery) {
  return <SetNewPassword onDone={() => setPasswordRecovery(false)} />;
}
  if (!session && !isGuest) {
    return <Auth onLogin={(type) => { if (type === 'guest') setIsGuest(true); }} />;
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', fontFamily: 'Arial, sans-serif' }}>
      <Navbar activePage={activePage} setActivePage={setActivePage} onSignOut={handleSignOut} userEmail={session ? session.user.email : 'Guest'} />
      <div style={{ padding: '16px', maxWidth: '1200px', margin: '0 auto' }}>
        {activePage === 'Home' && <Home setActivePage={setActivePage} />}
        {activePage === 'Cheat Sheet' && <CheatSheet />}
        {activePage === 'Deal Calculator' && <DealCalculator onSaveDeal={handleSaveDeal} />}
        {activePage === 'Auction Tracker' && <AuctionTracker />}
        {activePage === 'Auction Platforms' && <AuctionPlatforms />}
        {activePage === 'Due Diligence' && <DueDiligence deals={savedDeals} onUpdateDeal={handleUpdateDeal} onDeleteDeal={handleDeleteDeal} />}
        {activePage === 'AI Research' && <AIResearch />}
      </div>
    </div>
  );
}

export default App;
